import i18next from 'i18next';
import React, { ReactElement, useCallback, useEffect, useState } from 'react';
import Tab from 'react-bootstrap/Tab';
import Tabs from 'react-bootstrap/Tabs';
import { QueryClient, useQueryClient } from '@tanstack/react-query';
import { useAuthFetchFromRestAPI } from '../../util/api';
import PrimaryButton from '../../components/PrimaryButton';
import { EntityScope } from '../../../../shared/utils/enums';
import { useExtendedAuth } from '../../util/auth0';
import { isScopeKey, ManagementMode, ScopeKey } from '../../util/types';
import SearchBar from '../../components/SearchBar';
import { ExerciseTagDto } from '../../../../shared/dtos/exerciseTag.dto';
import { ExerciseDto } from '../../../../shared/dtos/exercise.dto';
import { PagedDto } from '../../../../shared/dtos/paged.dto';
import { ExerciseSearchRequest } from '../../../../shared/requests/exerciseSearch.request';
import { exerciseSearchQueryKeys } from '../../hooks/useExerciseSearch';
import { PagedQueryResult, usePagedQuery } from '../../hooks/usePagedQuery';
import ExerciseTable from './ExerciseTable';
import ExerciseModal from './ExerciseModal';

const PAGE_SIZE: number = 10;

type ExerciseVideoManagementProps = {
  mode: ManagementMode;
};

function getModalEntityScope(mode: ManagementMode): EntityScope {
  if (mode === 'superAdmin') {
    return EntityScope.CORE;
  }

  return EntityScope.TENANT;
}

const ExerciseManagement = ({ mode }: ExerciseVideoManagementProps): ReactElement => {
  const { authFetch } = useAuthFetchFromRestAPI();
  const { isSuperAdminUser } = useExtendedAuth();
  const queryClient: QueryClient = useQueryClient();

  const [isSuperAdmin, setIsSuperAdmin] = useState(mode === 'superAdmin');
  const [activeKey, setActiveKey] = useState<ScopeKey>('tenant');

  const [isLoadingTags, setIsLoadingTags] = useState(false);

  const [searchKey, setSearchKey] = useState('');
  const [tags, setTags] = useState<ExerciseTagDto[]>([]);

  const [showModal, setShowModal] = useState(false);
  const [editExercise, setEditExercise] = useState<ExerciseDto | null>(null);

  const isAdminMode: boolean = mode === 'admin';
  const modalEntityScope: EntityScope = getModalEntityScope(mode);

  useEffect(() => {
    if (mode === 'superAdmin') {
      setIsSuperAdmin(true);
      return;
    }

    void (async (): Promise<void> => {
      setIsSuperAdmin(await isSuperAdminUser());
    })();
  }, [isSuperAdminUser, mode]);

  const fetchTags: () => Promise<void> = useCallback(async () => {
    setIsLoadingTags(true);

    try {
      if (mode === 'superAdmin') {
        const result: ExerciseTagDto[] = await authFetch('/api/v1/exercise/tag', {
          method: 'GET',
          queryParameters: new URLSearchParams({ onlyCore: 'true' }),
        });

        setTags(Array.isArray(result) ? result : []);
        return;
      }

      const result: ExerciseTagDto[] = await authFetch('/api/v1/exercise/tag', { method: 'GET' });
      setTags(Array.isArray(result) ? result : []);
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoadingTags(false);
    }
  }, [authFetch, mode]);

  const fetchExercisePage = (scope: ScopeKey) =>
    async (page: number, pageSize: number): Promise<PagedDto<ExerciseDto>> => {
      const body: ExerciseSearchRequest = {
        page,
        pageSize,
        searchKey: searchKey || undefined,
      };

      return await authFetch(`/api/v1/exercise/${scope}/search`, { method: 'POST', body });
    };

  const tenantExercises: PagedQueryResult<ExerciseDto> = usePagedQuery({
    queryKey: exerciseSearchQueryKeys.managementList('tenant', searchKey),
    fetchPage: fetchExercisePage('tenant'),
    pageSize: PAGE_SIZE,
    enabled: isAdminMode,
  });

  const coreExercises: PagedQueryResult<ExerciseDto> = usePagedQuery({
    queryKey: exerciseSearchQueryKeys.managementList('core', searchKey),
    fetchPage: fetchExercisePage('core'),
    pageSize: PAGE_SIZE,
  });

  const handleSearch: (value: string) => void = useCallback((value: string): void => {
    setSearchKey(value.trim());
  }, []);

  useEffect(() => {
    void fetchTags();
  }, [fetchTags]);

  const closeModal = (): void => {
    setEditExercise(null);
    setShowModal(false);
  };

  const openCreateModal = (): void => {
    setEditExercise(null);
    setShowModal(true);
  };

  const openEditModal = (exercise: ExerciseDto): void => {
    setEditExercise(exercise);
    setShowModal(true);
  };

  const refreshExercises = async (): Promise<void> => {
    await queryClient.invalidateQueries({ queryKey: exerciseSearchQueryKeys.all });
  };

  const renderToolbar = (showCreateButton: boolean): ReactElement => {
    return (
      <>
        <SearchBar
          onSearch={handleSearch}
          placeholder={i18next.t('generic.search')}
          maxWidth={400}
        />

        {showCreateButton && (
          <PrimaryButton
            onClick={openCreateModal}
            disabled={isLoadingTags}>
            {i18next.t('generic.create')}
          </PrimaryButton>
        )}
      </>
    )
  };

  const renderExerciseTable = (
    exercises: PagedQueryResult<ExerciseDto>,
    canEdit: boolean,
    showCreateButton: boolean,
  ): ReactElement => {
    return (
      <ExerciseTable
        exercises={exercises.items}
        tags={tags}
        isLoading={isLoadingTags || exercises.isLoading}
        toolbar={renderToolbar(showCreateButton)}
        pagination={exercises}
        onClick={canEdit ? openEditModal : undefined}
      />
    );
  };

  const renderAdminContent = (): ReactElement => {
    return (
      <Tabs
        activeKey={activeKey}
        onSelect={(key) => {
          if (isScopeKey(key)) {
            setActiveKey(key);
          }
        }}
        className="mb-3 pt-3"
        justify
        mountOnEnter
        unmountOnExit>
        <Tab
          eventKey="tenant"
          title={i18next.t('adminExerciseVideo.tenantExercises')}>
          {renderExerciseTable(tenantExercises, true, isSuperAdmin)}
        </Tab>

        <Tab
          eventKey="core"
          title={i18next.t('adminExerciseVideo.coreExercises')}>
          {renderExerciseTable(coreExercises, false, false)}
        </Tab>
      </Tabs>
    )
  };

  return (
    <>
      <ExerciseModal
        show={showModal}
        entityScope={modalEntityScope}
        tags={tags}
        exerciseVideo={editExercise}
        isSuperAdmin={isSuperAdmin}
        onClose={closeModal}
        onSave={refreshExercises}
      />

      {isAdminMode ? renderAdminContent() : <div className="pt-3">{renderExerciseTable(coreExercises, true, true)}</div>}
    </>
  );
};

export default ExerciseManagement;
